import { signal, computed, effect, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject, Subscription, of, timer, throwError, EMPTY } from 'rxjs';
import { catchError, retry, timeout, take, takeUntil, finalize } from 'rxjs/operators';

/**
 * Ergebnis eines einzelnen Ladevorgangs, wie es das Polling zur Steuerung des
 * Backoffs auswertet:
 *  - `'ok'`        -> Daten erhalten (Fehlerzaehler wird zurueckgesetzt).
 *  - `'error'`     -> Fehler (Fehlerzaehler steigt -> laengeres Poll-Intervall).
 *  - `'cancelled'` -> Stream wurde abgebrochen (z. B. durch einen parallelen
 *                     load()/refresh()-Aufruf). Neutral: der Zaehler bleibt
 *                     unveraendert, der naechste Tick wird trotzdem geplant.
 */
type LoadOutcome = 'ok' | 'error' | 'cancelled';

/**
 * Error types for data operations
 */
export enum DataErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  PARSE_ERROR = 'PARSE_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

/**
 * Custom error class for data operations
 */
export class DataError extends Error {
  constructor(
    public type: DataErrorType,
    public originalError?: any,
    message?: string
  ) {
    super(message || `Data error: ${type}`);
    this.name = 'DataError';
  }
}

/**
 * Data state interface
 */
export interface DataState<T> {
  data: T[];
  loading: boolean;
  error: DataError | null;
  lastUpdated: number | null;
  backgroundRefreshing: boolean; 
}

/**
 * Konfiguration für das Daten-Fetching.
 *
 * Alle Felder sind optional; nicht gesetzte Felder fallen im Constructor auf die
 * angegebenen Defaults zurück.
 */
export interface DataServiceConfig {
  /**
   * Name des nutzenden Service (z. B. 'DashboardNewsService'). Wird allen Logzeilen
   * DIESER Instanz als Praefix beigestellt: `[DataService/DashboardNewsService]`.
   *
   * Notwendig, weil die fetch-Funktionen der Consumer bewusst NICHT mehr selbst
   * loggen (eine Fehlerzeile pro Fehler). Ohne den Namen waeren die Meldungen der
   * parallel pollenden Instanzen als blosses `[DataService]` nicht unterscheidbar.
   * @default '' (Praefix bleibt `[DataService]`)
   */
  name?: string;

  /**
   * Cache aktivieren. Bei true liefert load() gültige Cache-Daten sofort und
   * aktualisiert (falls enableBackgroundRefresh) im Hintergrund; bei false wird
   * immer frisch geladen.
   * @default false
   */
  cacheEnabled?: boolean;

  /**
   * Gültigkeitsdauer des Caches in Millisekunden (isCacheValid). Nur wirksam,
   * wenn cacheEnabled: true.
   * @default 300000 (5 Minuten)
   */
  cacheDuration?: number;

  /**
   * Timeout des ERSTEN Fetch-Versuchs in Millisekunden (timeout({ first })).
   * @default 15000 (15 Sekunden)
   */
  requestTimeout?: number;

  /**
   * Anzahl der WIEDERHOLUNGEN nach einem transienten Fehler (Timeout/Netzwerk/5xx;
   * siehe defaultRetryDelay). Gesamtversuche = maxRetries + 1.
   * @default 1 (= 2 Versuche total)
   */
  maxRetries?: number;

  /**
   * Strategie für die Wartezeit zwischen Retries. Wirft der Callback den Fehler
   * weiter, wird der Retry abgebrochen.
   * @default defaultRetryDelay (Exponential Backoff mit Cap + Equal Jitter,
   *          nur für transiente Fehler)
   */
  retryDelay?: (error: any, retryCount: number) => Observable<any>;

  /**
   * Aktualisiert bei einem Cache-Treffer die Daten zusätzlich im Hintergrund.
   * Begleit-Feature des Caches: greift ausschliesslich im Cache-Zweig von load()
   * und damit nur zusammen mit cacheEnabled: true.
   * @default false
   */
  enableBackgroundRefresh?: boolean;

  /**
   * Steuert, ob Fehler dieses Service als BENUTZERSICHTBARE Nachricht (Toast)
   * gemeldet werden sollen.
   *
   * Der DataService LOGGT Fehler unabhängig von diesem Flag immer in die Konsole
   * und setzt das error-Signal (fuer Inline-Widget-States). Dieses Flag ist ein
   * deklarativer Hinweis, den der CONSUMER (Component) auswertet, um einen Toast
   * anzuzeigen (true) oder zu unterdruecken und nur die Konsole zu nutzen (false).
   *
   * Globaler Default hier; pro Service ueber die eigene Config ueberschreibbar.
   * @default false (konservativ: nur Konsole, kein Toast)
   */
  showErrorMessages?: boolean;

  /**
   * Intervall (ms) fuer das timer-basierte Hintergrund-Polling (startPolling).
   * Jeder Tick loest ein load(fetchFn, true) = Single-Shot-Fetch aus.
   * @default 60000 (60 Sekunden)
   */
  pollInterval?: number;

  /**
   * Wartezeit (ms) bis zum Reload, wenn das Polling nach einer Pause WIEDER
   * AUFGENOMMEN wird — also bei Rueckkehr in einen versteckten Tab und bei
   * startPolling('interval') auf bereits veralteten Daten.
   *
   * Greift NUR in diesen beiden Faellen. Ein normaler Start (startPolling(fn) bzw.
   * start = 0) laedt weiterhin sofort, ebenso ein 'interval'-Start ohne jemals
   * geladene Daten – dort gibt es keinen Zustand, aus dem man zurueckkehrt.
   *
   * Bewusst nicht 0: der Nutzer kommt typischerweise aus einem anderen Tab/Fenster
   * zurueck, in dem er etwas geschrieben hat (Kurs beendet, News gelesen). Ein
   * sofortiger Fetch kann ein nachhinkendes Backend-Read-Modell erwischen und den
   * alten Stand zurueckspielen. Dieselbe Begruendung wie beim
   * #postWriteReloadDelayMs des NotificationsService.
   * @default 3000 (3 Sekunden)
   */
  resumeDelayMs?: number;
}

/**
 * Startzeitpunkt des ERSTEN Poll-Ticks (Parameter von startPolling):
 *  - `0`          -> sofort frisch laden (Default).
 *  - `n` (ms)     -> erster Tick nach n ms. Fuer fachliche Wartezeiten, die NICHTS
 *                    mit dem Takt zu tun haben (z. B. nach einem Write, damit ein
 *                    nachhinkendes Read-Modell nachziehen kann).
 *  - `'interval'` -> erster Tick zum naechsten regulaeren Termin, gerechnet ab dem
 *                    LETZTEN erfolgreichen Load (lastUpdated + pollInterval). Fuer
 *                    Consumer, die ihre Initialdaten schon selbst geladen haben und den
 *                    Doppel-Load beim Start vermeiden wollen – ohne das Intervall zu
 *                    kennen oder zu kopieren.
 *                    WICHTIG: Liegt dieser Termin bereits in der Vergangenheit (die
 *                    Daten sind also schon aelter als ein Intervall, z. B. weil das
 *                    Polling zwischenzeitlich gestoppt war), wird NICHT ein volles
 *                    Intervall gewartet, sondern nach resumeDelayMs geladen. Sonst
 *                    zeigte ein Consumer nach der Rueckkehr bis zu ein ganzes Intervall
 *                    lang veraltete Daten. Wurde noch NIE geladen, wird sofort geladen
 *                    (wie start = 0) – es gibt dann keinen Vorzustand.
 */
export type PollStart = number | 'interval';

/**
 * Optional polling behaviour (third parameter of startPolling).
 */
export interface PollOptions {
  /**
   * Checked right before every poll tick (regular ticks and visibility/focus
   * resumes). Returning true skips the tick without loading and retries after
   * resumeDelayMs. Needed because every load cancels the previous one
   * (cancelPreviousLoad$): e.g. a paged list must not lose an in-flight
   * "load more" request to a poll tick.
   * @default undefined (never skip)
   */
  skipTick?: () => boolean;

  /**
   * No timer ticks: the data is reloaded only when the user returns to the page
   * (tab visible again via visibilitychange, window focus), once per return after
   * resumeDelayMs. `start` of startPolling is ignored, there is no first tick. A
   * failed resume load is not retried; the next return loads again. For pages that
   * load on open and only need fresh data after the user was elsewhere.
   * @default false (regular interval polling)
   */
  resumeOnly?: boolean;
}

/**
 * Generic Data Service with Signals and Caching
 * 
 * Provides a unified approach for fetching and managing data with:
 * - Signal-based reactive state management
 * - Automatic caching with configurable duration
 * - Cache-first strategy: returns cached data immediately, refreshes in background
 * - Comprehensive error handling
 * - Automatic retry logic
 * - Type-safe data handling
 * 
 * Usage:
 * ```typescript
 * 
 * private readonly dataService = new DataService<BadgeDto>(
 *   {
 *     cacheEnabled: true,
 *     cacheDuration: 5 * 60 * 1000, // 5 minutes
 *     requestTimeout: 30000,
 *     maxRetries: 2
 *   }
 * );
 * 
 * // Load data (returns cached data immediately, refreshes in background)
 * this.dataService.load(() => this.fetchBadges());
 * 
 * // Access reactive signals
 * readonly data = this.dataService.data;
 * readonly loading = this.dataService.loading;
 * readonly error = this.dataService.error;
 * ```
 * 
 * @author 
 * @since 2.0.0
 */
export class DataService<T> {

  readonly #debug: boolean = false;

  // Configuration
  private readonly config: Required<DataServiceConfig>;
  private readonly destroyRef: DestroyRef | undefined;

  /**
   * Praefix aller Logzeilen dieser Instanz: `[DataService]` bzw.
   * `[DataService/<name>]`, wenn die Config einen Namen mitgibt.
   */
  readonly #logPrefix: string;

  // State Management with Signals
  private readonly dataState = signal<DataState<T>>({
    data: [],
    loading: false,
    error: null,
    lastUpdated: null,
    backgroundRefreshing: false,
  });

  // Public read-only computed signals
  readonly data = computed(() => this.dataState().data);
  readonly loading = computed(() => this.dataState().loading);
  readonly error = computed(() => this.dataState().error);
  readonly lastUpdated = computed(() => this.dataState().lastUpdated);
  readonly backgroundRefreshing = computed(() => this.dataState().backgroundRefreshing);
  
  // Cache validity check
  readonly isCacheValid = computed(() => {
    // Ohne aktivierten Cache gibt es keinen gueltigen Cache: lastUpdated wird zwar
    // weiterhin fortgeschrieben, aber nie als Cache gelesen (siehe load()).
    if (!this.config.cacheEnabled) return false;

    const state = this.dataState();
    if (!state.lastUpdated) return false;

    const now = Date.now();
    return (now - state.lastUpdated) < this.config.cacheDuration;
  });

  // Data statistics
  readonly count = computed(() => this.data().length);
  readonly isEmpty = computed(() => this.data().length === 0);
  readonly hasData = computed(() => this.data().length > 0);

  /**
   * Ob Fehler als benutzersichtbare Nachricht (Toast) gemeldet werden sollen.
   * Deklarativer Hinweis fuer den Consumer; siehe DataServiceConfig.showErrorMessages.
   */
  get showErrorMessages(): boolean {
    return this.config.showErrorMessages;
  }

  // Subject zum Abbrechen vorheriger Aufrufe
  private cancelPreviousLoad$ = new Subject<void>();

  // Laufende Timer-Polling-Subscription (Pattern B, siehe startPolling).
  #pollSub?: Subscription;
  // Aktuelle fetchFn des laufenden Pollings – fuer den Resume nach Tab-Rueckkehr.
  #pollFetchFn?: () => Observable<T[]>;
  // Optional skip predicate of the running polling (see PollOptions.skipTick).
  #pollSkipTick?: () => boolean;
  // Resume-only mode of the running polling (see PollOptions.resumeOnly).
  #pollResumeOnly = false;
  // visibilitychange-Listener, der das Polling bei verstecktem Tab pausiert.
  #visibilityHandler?: () => void;
  // focus-Listener am window als ZWEITES Resume-Signal. Notwendig, weil
  // visibilitychange browserabhaengig NICHT feuert, wenn der Nutzer aus einem anderen
  // FENSTER zurueckkommt (Chrome/macOS meldet ein verdecktes Fenster weiter als
  // 'visible'; Occlusion-Erkennung gibt es nur auf Windows/ChromeOS). Ob der
  // Media-Player als Tab oder Fenster aufgeht, ist zudem Browser- UND
  // Nutzereinstellung – auf ein einzelnes Signal darf man sich daher nicht verlassen.
  #focusHandler?: () => void;
  // true, wenn das Polling von AUSSEN gestoppt wurde (stopPolling), z. B. durch den
  // IntersectionObserver eines Consumers. Ein nachfolgendes startPolling('interval')
  // ist dann eine Wiederaufnahme und laedt zeitnah, statt den regulaeren Termin
  // abzuwarten. Siehe #intervalStartDelay.
  #pollInterrupted = false;
  // Zeitpunkt des letzten Resume-Reloads. Mindestabstand gegen Signal-Salven, wenn
  // mehrere Signale dasselbe Ereignis melden (visibilitychange UND focus).
  #lastResumeAt = 0;
  // true, solange das Polling wegen eines versteckten Tabs pausiert ist. Verhindert,
  // dass ein noch laufender Tick nach dem Pausieren den naechsten Tick nachplant.
  #pollPaused = false;
  // Anzahl der unmittelbar aufeinanderfolgenden fehlgeschlagenen Poll-Ticks.
  // Steuert das Backoff (siehe #nextPollDelay); ein Erfolg setzt den Wert zurueck.
  #pollFailures = 0;

  /**
   * Obergrenze der Backoff-Verdopplungen. Bei einem pollInterval von 60s ergibt
   * 2^4 ein maximales Intervall von 16 Minuten – genug, um ein unerreichbares
   * Backend nicht dauerhaft mit Requests zu belasten, und kurz genug, um sich
   * nach der Erholung zeitnah selbst zu fangen.
   */
  static readonly #POLL_MAX_BACKOFF_STEPS = 4;

  /**
   * Constructor
   * 
   * @param config - Configuration for the data service
   */
  constructor(config: DataServiceConfig, destroyRef?: DestroyRef) {

    // Default-Konfiguration. Nicht gesetzte Felder fallen auf die hier genannten
    // Defaults zurück (siehe DataServiceConfig für die Bedeutung je Feld).
    this.config = {
      // Identifiziert die Instanz in den Logs (siehe #logPrefix).
      name: config?.name ?? '',
      // Cache standardmässig AUS: die App nutzt DataService durchgehend als
      // Live-Datenquelle (alle Consumer setzen cacheEnabled: false).
      cacheEnabled: config?.cacheEnabled ?? false,
      // Nur relevant, wenn cacheEnabled: true. Gültigkeitsdauer des Caches.
      cacheDuration: config?.cacheDuration ?? 5 * 60 * 1000, // 5 minutes
      // Timeout des ERSTEN Fetch-Versuchs.
      requestTimeout: config?.requestTimeout ?? 15000, // 15 seconds
      // Anzahl der WIEDERHOLUNGEN nach einem (transienten) Fehler.
      // 1 = 1 Retry = 2 Versuche total.
      maxRetries: config?.maxRetries ?? 1,
      retryDelay: config?.retryDelay ?? this.defaultRetryDelay.bind(this),
      // Begleit-Feature des Caches (läuft nur im Cache-Zweig von load()); daher
      // konsistent zu cacheEnabled standardmässig AUS.
      enableBackgroundRefresh: config?.enableBackgroundRefresh ?? false,
      // Fehler standardmässig NUR in die Konsole; ein Service opted per
      // showErrorMessages: true bewusst in benutzersichtbare Toasts ein.
      showErrorMessages: config?.showErrorMessages ?? false,
      // Intervall des timer-basierten Hintergrund-Pollings (startPolling).
      pollInterval: config?.pollInterval ?? 60000,
      // Kurze Wartezeit beim Wiederaufnehmen des Pollings (Tab-Rueckkehr / veraltete
      // Daten), damit ein nachhinkendes Backend-Read-Modell nachziehen kann.
      resumeDelayMs: config?.resumeDelayMs ?? 3000
    };

    this.#logPrefix = this.config.name ? `[DataService/${this.config.name}]` : '[DataService]';

    if(destroyRef) {
      this.destroyRef = destroyRef;
    }

    // Optional: Log cache expiration. Nur sinnvoll, wenn der Cache ueberhaupt
    // gelesen wird (load()); bei cacheEnabled:false gibt es nichts, das ablaufen
    // koennte, und der Effect wuerde beim Poll-Tick nur Rauschen erzeugen.
    if (this.config.cacheEnabled) {
      effect(() => {
        const valid = this.isCacheValid();
        if (!valid && this.data().length > 0) {
          console.log(`${this.#logPrefix} Cache expired for ${this.data().length} items`);
        }
      });
    }
  }

  /**
   * Timer-basiertes Hintergrund-Polling: jeder Tick ist ein eigenstaendiger
   * Single-Shot-Fetch (Pattern B). Ein fehlgeschlagener Tick beendet NICHT das
   * Polling; der naechste Tick laedt erneut, und Fehler propagieren regulaer in das
   * error-Signal.
   *
   * Der naechste Tick wird jeweils nach Abschluss des vorherigen geplant
   * (#scheduleNextPoll). Regulaer im Abstand von pollInterval; nach
   * aufeinanderfolgenden Fehlern exponentiell verlaengert (#nextPollDelay), damit ein
   * unerreichbares Backend nicht dauerhaft im 60s-Takt beschossen wird. Innerhalb
   * eines Ticks wird NICHT wiederholt – der naechste Tick ist der Retry.
   * Ein erfolgreicher Tick setzt den Takt sofort auf pollInterval zurueck.
   *
   * Page Visibility: bei verstecktem Tab (document.hidden) pausiert das Polling und
   * nimmt bei Rueckkehr SOFORT wieder auf (frischer Load, da Daten in der
   * Zwischenzeit veraltet sein koennen). So laeuft im Hintergrund-Tab kein Polling.
   *
   * @param fetchFn Liefert die Daten (identisch zum load()-fetchFn).
   * @param start   Startzeitpunkt des ersten Ticks: 0 (Default) = sofort, n ms, oder
   *                'interval' = erst mit dem naechsten regulaeren Tick (s. PollStart).
   * @param options Optional behaviour, e.g. skipTick (see PollOptions).
   */
  startPolling(fetchFn: () => Observable<T[]>, start: PollStart = 0, options?: PollOptions): void {
    // Bewusst #teardownPolling() und NICHT stopPolling(): ein Neustart ist keine
    // Unterbrechung von aussen und darf #pollInterrupted nicht setzen.
    this.#teardownPolling();
    this.#pollFetchFn = fetchFn;
    this.#pollSkipTick = options?.skipTick;
    this.#pollResumeOnly = options?.resumeOnly ?? false;
    this.#pollPaused = false;
    this.#pollFailures = 0;

    if (typeof window !== 'undefined') {
      this.#focusHandler = () => this.#resumePoll('focus');
      window.addEventListener('focus', this.#focusHandler);
    }

    if (typeof document !== 'undefined') {
      this.#visibilityHandler = () => {
        if (document.hidden) {
          // Tab versteckt -> Polling pausieren (kein Fetch im Hintergrund).
          // #pollPaused verhindert zusaetzlich, dass ein noch laufender Tick
          // beim Settle den naechsten Tick nachplant.
          if (this.#debug) console.log(`${this.#logPrefix} visibilitychange -> hidden: Polling pausiert`);
          this.#pollPaused = true;
          this.#pollSub?.unsubscribe();
          this.#pollSub = undefined;
        } else {
          this.#resumePoll('visibilitychange');
        }
      };
      document.addEventListener('visibilitychange', this.#visibilityHandler);
    }

    // 'interval' hier auflösen: nur der DataService kennt das konfigurierte
    // pollInterval UND den Zeitpunkt des letzten Loads – Consumer sollen beides nicht
    // kopieren muessen.
    const initialDelayMs = start === 'interval' ? this.#intervalStartDelay() : start;

    // Verbraucht: die Information „war unterbrochen" gilt nur fuer DIESEN Neustart.
    this.#pollInterrupted = false;

    // Resume-only: no first tick and no timer – only the listeners above reload.
    if (this.#pollResumeOnly) {
      if (this.#debug) console.log(`${this.#logPrefix} startPolling(resumeOnly) -> reload only on return to the page`);
      return;
    }

    // Initial nur starten, wenn der Tab sichtbar ist; sonst uebernimmt der
    // visibilitychange-Handler den Start bei Rueckkehr.
    if (typeof document === 'undefined' || !document.hidden) {
      if (this.#debug) console.log(`${this.#logPrefix} startPolling(start=${String(start)}) -> erster Tick in ${initialDelayMs}ms`);
      this.#scheduleNextPoll(initialDelayMs);
    } else {
      if (this.#debug) console.log(`${this.#logPrefix} startPolling(start=${String(start)}) -> Tab versteckt, warte auf Rueckkehr`);
      this.#pollPaused = true;
    }
  }

  /**
   * Nimmt das Polling nach einer Unterbrechung zeitnah wieder auf – gemeinsamer Pfad
   * fuer ALLE Resume-Signale (visibilitychange, window-focus).
   *
   * Bewusst mehrere Signale: welches feuert, ist browser- und einstellungsabhaengig
   * (Tab vs. Fenster beim Oeffnen des Players; Occlusion-Erkennung nur auf
   * Windows/ChromeOS). Der Mindestabstand von resumeDelayMs verhindert, dass zwei
   * Signale zum gleichen Ereignis zwei Fetches ausloesen.
   */
  #resumePoll(reason: string): void {
    if (!this.#pollFetchFn) {
      // Passiert, wenn zwischenzeitlich stopPolling() lief (z. B. durch den
      // IntersectionObserver eines Consumers). Die Wiederaufnahme laeuft dann ueber
      // startPolling('interval') + #pollInterrupted, nicht ueber diesen Pfad.
      if (this.#debug) console.log(`${this.#logPrefix} ${reason} -> kein Resume: Polling ist gestoppt`);
      return;
    }

    const now = Date.now();
    if (now - this.#lastResumeAt < this.config.resumeDelayMs) {
      if (this.#debug) console.log(`${this.#logPrefix} ${reason} -> Resume uebersprungen (vor ${now - this.#lastResumeAt}ms schon eines)`);
      return;
    }

    this.#lastResumeAt = now;
    this.#pollPaused = false;
    if (this.#debug) console.log(`${this.#logPrefix} ${reason} -> Reload in ${this.config.resumeDelayMs}ms`);
    this.#scheduleNextPoll(this.config.resumeDelayMs);
  }

  /**
   * Plant den NAECHSTEN Poll-Tick als Einzel-Timer und plant sich nach dessen
   * Abschluss selbst neu.
   *
   * Bewusst kein periodischer timer(delay, interval): nur so kann das Intervall
   * pro Tick aus #nextPollDelay() kommen und damit bei anhaltenden Fehlern
   * zurueckgehen (Backoff). Der Tick laeuft OHNE internen Retry (maxRetries 0) –
   * bei einem Poll ist der naechste Tick der Retry, ein Retry innerhalb des Ticks
   * wuerde die Last gegen ein ohnehin gestoertes Backend nur vervielfachen.
   *
   * Der Tick plant in JEDEM Fall nachfolgend weiter (auch bei Fehler/Abbruch),
   * solange das Polling nicht gestoppt oder pausiert wurde – das Polling stirbt
   * also nicht an einem einzelnen fehlgeschlagenen Tick.
   */
  #scheduleNextPoll(delayMs: number): void {
    const fetchFn = this.#pollFetchFn;
    if (!fetchFn) return;

    if (this.#debug) console.log(`${this.#logPrefix} naechster Tick in ${delayMs}ms`);

    this.#pollSub?.unsubscribe();

    const source$ = timer(delayMs);
    const guarded$ = this.destroyRef
      ? source$.pipe(takeUntilDestroyed(this.destroyRef))
      : source$;

    this.#pollSub = guarded$.subscribe(() => {
      // The consumer has a request of its own in flight that this tick would cancel
      // (cancelPreviousLoad$). Skip without loading and retry soon, so a resume is
      // not postponed by a whole interval.
      if (this.#pollSkipTick?.()) {
        if (this.#debug) console.log(`${this.#logPrefix} tick skipped (skipTick)`);
        if (this.#pollFetchFn && !this.#pollPaused) {
          this.#scheduleNextPoll(this.config.resumeDelayMs);
        }
        return;
      }

      this.#runLoad(fetchFn, true, 0, (outcome) => {
        if (outcome === 'ok') {
          this.#pollFailures = 0;
        } else if (outcome === 'error') {
          this.#pollFailures++;
        }
        // 'cancelled' ist neutral (paralleler load()/refresh() hat den Tick
        // abgebrochen) – Zaehler unveraendert, aber weiterplanen.

        // Resume-only: the resume load is the whole job, no follow-up tick.
        if (this.#pollFetchFn && !this.#pollPaused && !this.#pollResumeOnly) {
          this.#scheduleNextPoll(this.#nextPollDelay());
        }
      });
    });
  }

  /**
   * Loest startPolling(..., 'interval') in eine konkrete Wartezeit auf.
   *
   * 'interval' bedeutet „nicht doppelt laden, was gerade geladen wurde" – NICHT
   * „warte stur ein Intervall". Gerechnet wird daher ab dem letzten erfolgreichen
   * Load: verbleibt davon noch Zeit, wird nur diese Restzeit gewartet.
   *
   * Ist die Restzeit aufgebraucht (Daten aelter als ein Intervall), war das Polling
   * offensichtlich unterbrochen – dann wird nach resumeDelayMs geladen statt ein
   * volles Intervall zu warten. Genau dieser Fall trat beim LastCourse-Widget auf: der
   * IntersectionObserver stoppt das Polling, wenn der Tab versteckt wird (Section
   * gilt als unsichtbar), was AUCH den visibilitychange-Listener abraeumt. Bei der
   * Rueckkehr war der Neustart dann ein 'interval'-Start – und die Karte zeigte bis
   * zu 60s den alten Fortschritt.
   *
   * resumeDelayMs greift bewusst NUR beim Wiederaufnehmen. Wurde noch nie geladen,
   * gibt es keinen Vorzustand, aus dem man „zurueckkommt": dann verhaelt sich der
   * Aufruf wie ein normaler Start und laedt sofort (0).
   */
  #intervalStartDelay(): number {
    const lastUpdated = this.dataState().lastUpdated;

    // Erststart ohne Vorzustand -> sofort laden, wie bei startPolling(fn, 0).
    if (lastUpdated === null) {
      if (this.#debug) console.log(`${this.#logPrefix} 'interval': nie geladen -> 0ms`);
      return 0;
    }

    const age = Date.now() - lastUpdated;

    // Wiederaufnahme nach einem stopPolling() von aussen: der Nutzer kommt gerade
    // zurueck (Tab/Fenster/Sichtbarkeit der Section), es kann sich etwas geaendert
    // haben. Nicht den regulaeren Termin abwarten – das war der gemeldete Bug: bei
    // 25s Abwesenheit blieben noch 35s Restzeit stehen.
    if (this.#pollInterrupted) {
      if (this.#debug) console.log(`${this.#logPrefix} 'interval': Wiederaufnahme nach Unterbrechung (Daten ${age}ms alt) -> ${this.config.resumeDelayMs}ms`);
      this.#lastResumeAt = Date.now();
      return this.config.resumeDelayMs;
    }

    const remaining = this.config.pollInterval - age;

    // Restzeit offen -> nur diese abwarten (kein Doppel-Load direkt nach dem
    // Initial-Load des Consumers).
    if (remaining > 0) {
      if (this.#debug) console.log(`${this.#logPrefix} 'interval': Daten ${age}ms alt -> Restzeit ${remaining}ms`);
      return remaining;
    }

    if (this.#debug) console.log(`${this.#logPrefix} 'interval': Daten ${age}ms alt (aelter als pollInterval) -> ${this.config.resumeDelayMs}ms`);
    return this.config.resumeDelayMs;
  }

  /**
   * Wartezeit bis zum naechsten Poll-Tick: das konfigurierte pollInterval,
   * bei aufeinanderfolgenden Fehlern exponentiell verlaengert und bei
   * #POLL_MAX_BACKOFF_STEPS Verdopplungen gedeckelt.
   */
  #nextPollDelay(): number {
    const steps = Math.min(this.#pollFailures, DataService.#POLL_MAX_BACKOFF_STEPS);
    const delay = this.config.pollInterval * Math.pow(2, steps);
    if (steps > 0) {
      console.warn(`${this.#logPrefix} ${this.#pollFailures} consecutive poll failures – next poll in ${Math.round(delay / 1000)}s`);
    }
    return delay;
  }

  /**
   * Stoppt das timer-basierte Hintergrund-Polling (Pattern B) und raeumt die Listener ab.
   *
   * Merkt sich, DASS gestoppt wurde: ein spaeteres startPolling('interval') gilt dann
   * als Wiederaufnahme und laedt nach resumeDelayMs, statt den regulaeren Termin
   * abzuwarten (siehe #intervalStartDelay). Genau dieser Pfad tritt beim LastCourse-
   * Widget auf, dessen IntersectionObserver beim Tab-Wechsel stoppt und bei der
   * Rueckkehr neu startet.
   */
  stopPolling(): void {
    this.#pollInterrupted = this.#pollFetchFn !== undefined;
    if (this.#debug && this.#pollInterrupted) console.log(`${this.#logPrefix} stopPolling`);
    this.#teardownPolling();
  }

  /**
   * Raeumt Timer und Listener ab, OHNE #pollInterrupted zu setzen.
   * Fuer den internen Neustart aus startPolling() – der ist keine Unterbrechung.
   */
  #teardownPolling(): void {
    this.#pollSub?.unsubscribe();
    this.#pollSub = undefined;
    if (this.#visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.#visibilityHandler);
    }
    this.#visibilityHandler = undefined;
    if (this.#focusHandler && typeof window !== 'undefined') {
      window.removeEventListener('focus', this.#focusHandler);
    }
    this.#focusHandler = undefined;
    // Zuletzt: #pollFetchFn ist der Aktiv-Schalter, den ein noch laufender Tick
    // beim Settle prueft – ohne dieses Zuruecksetzen wuerde er nachplanen.
    this.#pollFetchFn = undefined;
    this.#pollSkipTick = undefined;
    this.#pollResumeOnly = false;
    this.#pollPaused = false;
    this.#pollFailures = 0;
  }

  /**
   * Load data with cache-first strategy
   * 
   * Returns cached data immediately if available and valid.
   * Starts a background refresh to fetch fresh data.
   * 
   * @param fetchFn - Function that returns Observable with data
   * @param forceRefresh - Force cache refresh
   */
  load(
    fetchFn: () => Observable<T[]>,
    forceRefresh: boolean = false
  ): void {
    this.#runLoad(fetchFn, forceRefresh, this.config.maxRetries);
  }

  /**
   * Interner Load-Kern.
   *
   * Gegenueber load() zusaetzlich parametrierbar, was das Polling braucht:
   * @param maxRetries Anzahl der Wiederholungen fuer DIESEN Aufruf. Das Polling
   *   uebergibt 0 – dort ist der naechste Tick der Retry (siehe #scheduleNextPoll).
   * @param onSettled Wird GENAU EINMAL mit dem Ergebnis des Aufrufs gerufen, auch
   *   wenn der Stream abgebrochen wurde ('cancelled'). Diese Garantie ist die
   *   Voraussetzung dafuer, dass die Poll-Kette nie stillschweigend abreisst.
   */
  #runLoad(
    fetchFn: () => Observable<T[]>,
    forceRefresh: boolean,
    maxRetries: number,
    onSettled?: (outcome: LoadOutcome) => void
  ): void {

    // Genau-einmal-Semantik fuer onSettled: das finalize() unten feuert auch nach
    // next/error und darf das dort gemeldete Ergebnis nicht ueberschreiben.
    let settled = false;
    const settle = (outcome: LoadOutcome): void => {
      if (settled) return;
      settled = true;
      onSettled?.(outcome);
    };

    // Laufende alte Streams (auch Polling) sofort abbrechen!
    this.cancelPreviousLoad$.next();

    if (this.#debug) console.warn(`${this.#logPrefix} load, fetchFn=`, fetchFn, ', forceRefresh=', forceRefresh);

    // Check cache validity
    if (this.config.cacheEnabled && this.isCacheValid() && !forceRefresh) {
      if (this.#debug) console.log(`${this.#logPrefix} Using cached data`);
      // Clear any previous errors when using cached data
      this.dataState.update(state => ({
        ...state,
        error: null
      }));
      // Start background refresh
      this.refreshInBackground(fetchFn);
      // Gueltiger Cache = erfolgreich beantwortet (kein Fehler, kein Backoff).
      settle('ok');
      return;
    }

    if (this.#debug) console.log(`${this.#logPrefix} Loading data...`);

    // Set loading state and clear previous errors
    this.dataState.update(state => ({
      ...state,
      loading: true,
      error: null
    }));

    let pipe$ = fetchFn();

    // Single-Shot pro Aufruf: fetch/Poll-Tick liefert genau eine Emission (Pattern B).
    pipe$ = pipe$.pipe(take(1));

    pipe$ = pipe$.pipe(
      // Timeout nur auf den ersten Aufruf anwenden
      timeout({ first: this.config.requestTimeout }),

      retry({
        count: maxRetries,
        delay: (error, retryCount) => {
          return this.config.retryDelay(error, retryCount);
        }
      }),

      catchError((error) => {
        // Geloggt wird ausschliesslich in handleError() – EINE Fehlerzeile pro
        // fehlgeschlagenem Versuch, nicht drei (vormals hier + dort + im
        // error-Handler der Subscription).
        this.handleError(error);
        settle('error');
        // EMPTY statt of([]): kein next-Emit -> updateState() läuft NICHT und
        // löscht den soeben gesetzten Fehler nicht wieder. So bleibt error gesetzt
        // (loading=false via handleError), womit das Widget einen Fehler von einer
        // leeren Erfolgsantwort unterscheiden kann.
        return EMPTY;
      }),
      // Horcht auf den Abbruch-Befehl für DIESEN spezifischen Stream
      takeUntil(this.cancelPreviousLoad$),
      // Sicherheitsnetz fuer onSettled: greift, wenn der Stream OHNE next/error
      // endet (Abbruch via cancelPreviousLoad$ oder takeUntilDestroyed).
      finalize(() => settle('cancelled'))
    );

    if (this.destroyRef) {
      pipe$ = pipe$.pipe(takeUntilDestroyed(this.destroyRef));
    }

    pipe$.subscribe({
      next: (data) => {
        if (this.#debug) console.log(`${this.#logPrefix} Loading data..., data=${data}, in ${data.length} items.`);
        this.updateState(data);
        settle('ok');
      },
      error: (error) => {
        // Praktisch unerreichbar (catchError oben faengt bereits alles ab), als
        // Sicherheitsnetz aber belassen. Logging ebenfalls nur via handleError().
        this.handleError(error);
        settle('error');
      }
    });

  }

  /**
   * Refresh data in background
   * 
   * Fetches fresh data without blocking the UI.
   * Updates state when data arrives.
   * 
   * @param fetchFn - Function that returns Observable with data
   */
  private refreshInBackground(fetchFn: () => Observable<T[]>): void {

    if (!this.config.enableBackgroundRefresh) {
      return;
    }

    if (this.#debug) console.log(`${this.#logPrefix} Refreshing data in background...`);

    // Background-Refresh State setzen
    this.dataState.update(state => ({
      ...state,
      backgroundRefreshing: true
    }));

    let observable$ = fetchFn();

    // GraphQL sind immer Single-Value Observables, daher take(1)!
    //observable$ = observable$.pipe(take(1));

    //fetchFn()
    observable$
      .pipe(
        // take(1) auch hier
        take(1),
        timeout(this.config.requestTimeout),
        retry({
          count: this.config.maxRetries,
          delay: (error, retryCount) => {
            if (this.#debug) console.warn(`${this.#logPrefix} Background refresh retry ${retryCount}/${this.config.maxRetries}`, error);
            return this.config.retryDelay(error, retryCount);
          }
        }),
        catchError((error) => {
          console.warn(`${this.#logPrefix} Background refresh failed after retries:`, error);
          // null signalisiert Fehler ohne Error-State zu setzen
          return of(null);
        }),
        //takeUntilDestroyed(this.destroyRef)
        // A newer load()/refresh()/poll tick supersedes this background refresh:
        // its late response must not overwrite the newer data (e.g. a paged list
        // after "load more" or a filter change).
        takeUntil(this.cancelPreviousLoad$),
        // Covers the cancelled case, in which neither next nor error runs.
        finalize(() => {
          if (this.dataState().backgroundRefreshing) {
            this.dataState.update(state => ({ ...state, backgroundRefreshing: false }));
          }
        })
      )
      .subscribe({
        next: (data) => {
          if (data !== null) {
            this.updateState(data);
            if (this.#debug) console.log(`${this.#logPrefix} Refreshed ${data}`);
          }
          else {

            // Auch bei Fehler backgroundRefreshing zurücksetzen
            this.dataState.update(state => ({
              ...state,
              backgroundRefreshing: false
            }));

          }

        },
        error: (error) => {
          console.warn(`${this.#logPrefix} Background refresh failed:`, error);
          // WICHTIG: Nicht den Error-State setzen bei Background-Refresh
          // Cached Daten bleiben gültig
          // Nur warnen, aber nicht den Error-State überschreiben
          this.dataState.update(state => ({
            ...state,
            backgroundRefreshing: false, // ✅ AUCH hier backgroundRefreshing zurücksetzen
            error: null // Error nicht setzen, da wir cached Daten haben
          }));
        }
      });
  }

  /**
   * Force reload data (clear cache and fetch)
   */
  forceRefresh(fetchFn: () => Observable<T[]>): void {
    if (this.#debug) console.log(`${this.#logPrefix} Force refreshing data...`);
    this.load(fetchFn, true);
  }

  /**
   * Clear data cache and state
   */
  clearCache(): void {
    this.dataState.set({
      data: [],
      loading: false,
      error: null,
      lastUpdated: null,
      backgroundRefreshing: false
    });
    if (this.#debug) console.log(`${this.#logPrefix} Cache cleared`);
  }

  /**
   * Get data as Observable (for backward compatibility)
   */
  getData$(): Observable<T[]> {
    return of(this.data());
  }

  /**
   * Optimistisches lokales Update der Daten ohne Server-Roundtrip.
   *
   * Erzeugt über den Updater eine neue Daten-Referenz (immutable) und ersetzt
   * damit den aktuellen data-Stand. Nützlich, um z. B. isRead sofort zu setzen,
   * bevor/ohne dass ein Refetch die Änderung bestätigt. Ein späterer load()/refresh()
   * überschreibt das Ergebnis wieder mit dem Server-Stand.
   */
  updateData(updater: (data: T[]) => T[]): void {
    this.dataState.update(state => ({ ...state, data: updater(state.data) }));
  }

  /**
   * Update state with new data
   */
  private updateState(data: T[]): void {
    this.dataState.update(state => ({
      ...state,
      data,
      loading: false,
      error: null,
      lastUpdated: Date.now(),
      backgroundRefreshing: false
    }));
  }

  /**
   * Handle errors
   */
  private handleError(error: any): void {
    let dataError: DataError;

    if (error.name === 'TimeoutError') {
      dataError = new DataError(
        DataErrorType.TIMEOUT_ERROR,
        error,
        'Request timeout while fetching data'
      );
    } else if (error.networkError) {
      dataError = new DataError(
        DataErrorType.NETWORK_ERROR,
        error,
        'Network error while fetching data'
      );
    } else if (error instanceof DataError) {
      dataError = error;
    } else {
      dataError = new DataError(
        DataErrorType.UNKNOWN_ERROR,
        error,
        'Unknown error while fetching data'
      );
    }

    // EINZIGE Fehler-Logstelle des Ladepfads: enthaelt daher auch die Apollo-Details,
    // die vormals in den (jetzt entfernten) Zusatz-Logs standen.
    console.error(`${this.#logPrefix} Error:`, {
      type: dataError.type,
      message: dataError.message,
      graphQLErrors: error?.graphQLErrors, // konkrete GraphQL-Fehler inkl. Feld/Pfad
      networkError: error?.networkError,   // HTTP-/Verbindungsfehler
      originalError: dataError.originalError
    });

    this.dataState.update(state => ({
      ...state,
      loading: false,
      backgroundRefreshing: false,  // ✅ Auch bei Error zurücksetzen
      error: dataError
    }));
  }

  /**
   * Default retry delay with exponential backoff.
   *
   * Wiederholt NUR transiente Fehler (Timeout, Netzwerk-/Verbindungsfehler, HTTP 5xx).
   * Nicht-transiente Fehler (GraphQL-Fehler, HTTP 4xx wie 401/403/400) werden sofort
   * durchgereicht – ein Retry würde daran nichts ändern und nur die Fehleranzeige verzögern.
   */
  private defaultRetryDelay(error: any, retryCount: number): Observable<any> {
    if (!this.isTransientError(error)) {
      console.warn(`${this.#logPrefix} Non-transient error – aborting retries:`, error?.message ?? error);
      // Fehler im delay-Callback weiterwerfen => retry() bricht ab und der Fehler
      // propagiert direkt zu catchError/handleError.
      return throwError(() => error);
    }

    // Exponentielles Backoff mit Cap (Obergrenze), damit die Wartezeit bei
    // höheren retryCount nicht unbegrenzt wächst.
    const BASE_DELAY_MS = 1000;
    const MAX_DELAY_MS = 10000;
    const capped = Math.min(Math.pow(2, retryCount - 1) * BASE_DELAY_MS, MAX_DELAY_MS);

    // Equal Jitter: die halbe (fixe) Basis + eine zufällige zweite Hälfte.
    // => Wartezeit liegt gleichverteilt in [capped/2, capped), nie zu kurz.
    const delay = capped / 2 + Math.random() * (capped / 2);

    console.warn(`${this.#logPrefix} Retrying (${retryCount}/${this.config.maxRetries}) after ~${Math.round(delay)}ms...`);
    return timer(delay);
  }

  /**
   * Klassifiziert einen Fehler als transient (wiederholenswert) oder nicht.
   *
   * Transient  -> Timeout, Netzwerk-/Verbindungsfehler, HTTP 5xx, HTTP 429
   * Nicht      -> GraphQL-Fehler, HTTP 4xx (Client-/Auth-Fehler), Unbekanntes (konservativ)
   */
  private isTransientError(error: any): boolean {
    // Timeout des timeout()-Operators
    if (error?.name === 'TimeoutError') {
      return true;
    }

    // Apollo networkError: HTTP-/Verbindungsfehler
    const networkError = error?.networkError;
    if (networkError) {
      const status: unknown = networkError.status ?? networkError.statusCode;
      if (typeof status === 'number') {
        // 4xx (ausser 429) sind Client-/Auth-Fehler -> nicht wiederholen
        return status === 429 || status >= 500;
      }
      // Kein Status (z. B. Verbindungsabbruch, DNS, Offline) -> transient
      return true;
    }

    // Reine GraphQL-Fehler (Validierung/Business) -> nicht wiederholen
    if (Array.isArray(error?.graphQLErrors) && error.graphQLErrors.length > 0) {
      return false;
    }

    // Unbekannter Fehler -> konservativ nicht wiederholen
    return false;
  }
}
